import React from 'react';
import withWorkflow from './withWorkflow';
import withPricing from './withPricing';
import RemoveRequisition from '../requisition/RemoveRequisition';
import FieldLevelHelp from './FieldLevelHelp';
import { isFullyAccessioned } from '../util/gearOrderUtil';

/**
 * This component renders the shopping cart icon and expandable details.
 */
class ShoppingCart extends React.Component {
	/**
	 * Constructor binds functions.
	 *
	 * @param props the properties
	 */
	constructor(props) {
		super(props);

		// We don't want to show price for International requisitions
		let domesticRequisitionType = false;
		if (this.props.gearOrder && this.props.gearOrder.practitioner && this.props.gearOrder.practitioner.office) {
			const office = this.props.gearOrder.practitioner.office;
			domesticRequisitionType = office.reqLayoutId === 0 || office.reqLayoutId === 2;
		}

		this.state = {
			displayRemoveRequisition: false,
			reqToDelete: null,
			domesticRequisitionType: domesticRequisitionType
		};

		this.editRequisition = this.editRequisition.bind(this);
		this.deleteRequisition = this.deleteRequisition.bind(this);

		this.displayRequisitionDeleteDialog = this.displayRequisitionDeleteDialog.bind(this);
		this.removeRequisitionResponseHandler = this.removeRequisitionResponseHandler.bind(this);
	}

	/**
	 * Recalculate the domesticRequisitionType value when the office's reqLayoutId changes.
	 * @param prevProps
	 */
	componentDidUpdate(prevProps) {
		const prevLayoutType = (((prevProps.gearOrder || {}).practitioner || {}).office || {}).reqLayoutId;
		const newLayoutType = (((this.props.gearOrder || {}).practitioner || {}).office || {}).reqLayoutId;
		if (prevLayoutType !== newLayoutType) {
			this.setState({ domesticRequisitionType: newLayoutType === 0 || newLayoutType === 2 });
		}
	}

	/**
	 * Displays the modal to delete a requisition
	 *
	 * @param requisition The requisition to delete
	 */
	displayRequisitionDeleteDialog(requisition) {
		this.setState({
			displayRemoveRequisition: true,
			reqToDelete: requisition
		});
	}

	/**
	 * Handles the response from removing a requisition from an order.
	 *
	 * @param response the fetch response
	 */
	removeRequisitionResponseHandler(response) {
		if (response.ok) {
			response
				.json()
				.then(json => {
					this.deleteRequisition(json, this.state.reqToDelete.id);
				})
				.catch(e => {
					this.props.showError(
						'Sorry, an unexpected error has occcured. If you continue to receive this message, please contact us.'
					);
				});
		} else {
			switch (response.status) {
				case 400:
					this.props.showError('Sorry, this requisition cannot be removed');
					break;
				case 403:
					this.props.showError('Sorry, you are not authorized to remove this requisition');
					break;
				case 404:
					this.props.showError('Sorry, this requisition cannot be found');
					break;
				default:
					this.props.showError(
						'Sorry, an unexpected error has occcured. If you continue to receive this message, please contact us.'
					);
			}
		}
	}

	/**
	 * Updates the order's active requisition ID and routes to the panel step
	 *
	 * @param gearReqId The id of the requisition to edit
	 */
	editRequisition(gearReqId) {
		this.props.setOmsRequisition(null);
		this.props.setActiveGearRequisitionId(gearReqId);
		this.props.updateStep(this.props.gearOrder, 'panel', null, gearReqId);
	}

	/**
	 * Removes a requisition from the gearOrder object.
	 * If the removed requisition is the current active requisition find the first
	 * requisition ID from the updated order (or null if there are none) and route
	 * to the panel step.
	 *
	 * @param updatedGearOrder The gearOrder after the requisition was deleted
	 * @param gearReqId The id of the requisition to delete
	 */
	deleteRequisition(updatedGearOrder, gearReqId) {
		const activeReqId = Number(this.props.gearReqId);
		this.props.setGearOrder(updatedGearOrder);
		if (activeReqId === gearReqId || !updatedGearOrder.requisitions.length) {
			let newActiveReqId = updatedGearOrder.requisitions.length > 0 ? updatedGearOrder.requisitions[0].id : null;
			this.props.setOmsRequisition(null);
			this.props.setActiveGearRequisitionId(newActiveReqId);

			/* For patients, if there are no more reqs on the order set addNewReq
			 *  so the panels step will show the search fields.
			 */
			if (this.props.userInfo.type === 'prc' && updatedGearOrder.requisitions.length === 0) {
				this.props.setAddNewReq(true);
			}

			this.props.updateStep(updatedGearOrder, 'panel');
		}
	}

	render() {
		if (!this.props.gearOrder || this.props.gearOrder.id === 0) {
			return null;
		}

		// Do not show the cart if all requisitions on the order are accessioned
		if (isFullyAccessioned(this.props.gearOrder)) {
			return null;
		}

		let practConfirmStep = this.props.getWorkflowStep('practitioner_confirmation');
		let patientConfirmStep = this.props.getWorkflowStep('patient_confirmation');
		let practitionerConfirmed = practConfirmStep && practConfirmStep.completed;
		let patientConfirmed = patientConfirmStep && patientConfirmStep.completed;
		let canEdit = this.props.userInfo.adminUser === 'true' ||
			( !practitionerConfirmed && (this.props.userInfo.type !== 'prc' || !patientConfirmed) );

		let totalDueToday = 0;
		let configErrorFound =
			this.props.pricingError || this.props.prices.find(price => price.configError || price.error) !== undefined;
		let loadedPrices = this.props.prices.filter(price => !price.placeholder);
		let reqsRequiringPrices = this.props.gearOrder.requisitions.filter(
			req => req.billingMethod !== null && req.billingMethod.indexOf('Physician') === -1
		);
		let allPricesLoaded = loadedPrices.length === reqsRequiringPrices.length;
		if (configErrorFound) {
			totalDueToday = (
				<FieldLevelHelp
					trigger="hover"
					textClassName="text-warning"
					iconClassName="fa-exclamation-triangle"
					helpText="Unable to determine price."
					large="true"
				/>
			);
		} else if (allPricesLoaded) {
			if (
				this.props.gearOrder.medicareBillingNoInsurance &&
				this.props.prices.find(
					price =>
						price.billingMethod.indexOf('Medicare') !== -1 && price.medicarePrice !== price.tricarePrice
				) !== undefined
			) {
				totalDueToday = (
					<FieldLevelHelp
						trigger="hover"
						textClassName="text-warning"
						iconClassName="fa-exclamation-triangle"
						helpText="Price cannot be determined until the Insurance step is completed."
						large="true"
					/>
				);
			} else {
				totalDueToday = this.props.prices
					.map(el => el.price)
					.reduce(
						(accumulator, currentValue) => accumulator + parseFloat(Math.round(currentValue * 100) / 100),
						0
					);
				totalDueToday = '$' + parseFloat(Math.round(totalDueToday * 100) / 100).toFixed(2);
			}
		} else {
			totalDueToday = <i className="fa fa-spinner fa-spin fa-fw"></i>;
		}

		let shoppingCartCount;
		if (this.props.gearOrder.requisitions) {
			if (this.props.gearOrder.containsShipToOfficeRequisition) {
				shoppingCartCount = this.props.gearOrder.requisitions.reduce((acc, req) => req.quantity + acc, 0);
			} else {
				shoppingCartCount = this.props.gearOrder.requisitions.length;
			}
		}

		return (
			<div className="dropdown">
				<button
					className="btn shopping-cart"
					id="dLabel"
					type="button"
					data-toggle="dropdown"
					aria-haspopup="true"
					aria-expanded="false"
				>
					<span>
						<i className="fa fa-shopping-cart padding-right-sm"></i>
					</span>
					<span className="padding-right-sm">{shoppingCartCount}</span>
					<span className="caret"></span>
				</button>

				<div className="panel panel-primary dropdown-menu shopping-cart-detail" aria-labelledby="dLabel">
					<div className="panel-heading">
						<h3 className="panel-title">Order #{this.props.gearOrder.id}</h3>
					</div>
					<table className="table table-condensed">
						<colgroup>
							<col style={{ width: '90px' }} />
							<col />
							<col style={{ width: '90px' }} />
						</colgroup>
						<tbody>
							{this.props.gearOrder.requisitions.map(req => {
								if (this.props.gearOrder.containsShipToOfficeRequisition) {
									return (
										<tr key={req.id}>
											<td>
												<span>Qty: {req.quantity}</span>
											</td>
											<td>{req.omsOrderableItemName}</td>
											<td>
												{canEdit && (
													<span>
														<button
															type="button"
															className="shipping-cart-action btn btn-link btn-unstyled"
															title="Edit"
															onClick={() => this.editRequisition()}
														>
															<i className="fa fa-pencil-square-o padding-x-xs"></i>
														</button>
														<button
															type="button"
															className="shipping-cart-action btn btn-link btn-unstyled"
															title="Remove"
															onClick={() => this.displayRequisitionDeleteDialog(req)}
														>
															<i className="fa fa-trash-o padding-x-xs"></i>
														</button>
													</span>
												)}
											</td>
										</tr>
									);
								}

								let price = this.props.prices.find(price => price.gearRequisitionId === req.id);
								let insuranceRequiredForPayment =
									this.props.gearOrder.medicareBillingNoInsurance &&
									req.medicareTricareBillingSelected &&
									price &&
									price.medicarePrice !== price.tricarePrice;
								let priceMarkup = null;
								let breakdown = null;
								if (req.physicianBillingSelected) {
									priceMarkup = (
										<FieldLevelHelp
											trigger="hover"
											textClassName="text-info"
											iconClassName="fa-info-circle"
											helpText="Physician account billing selected."
											large="true"
										/>
									);
								} else if (this.props.doNotQuotePrices) {
									priceMarkup = (
										<FieldLevelHelp
											trigger="hover"
											textClassName="text-info"
											iconClassName="fa-info-circle"
											helpText="Your practitioner has asked us to refer pricing questions to their office. Please contact your practitioner for payment amounts."
											large="true"
										/>
									);
								} else if (price && price.error) {
									priceMarkup = (
										<FieldLevelHelp
											trigger="hover"
											textClassName="text-warning"
											iconClassName="fa-exclamation-triangle"
											helpText="Unable to determine price."
											large="true"
										/>
									);
								} else if (price && price.placeholder) {
									priceMarkup = <i className="fa fa-spinner fa-spin fa-fw"></i>;
								} else if (insuranceRequiredForPayment) {
									priceMarkup = (
										<FieldLevelHelp
											trigger="hover"
											textClassName="text-warning"
											iconClassName="fa-exclamation-triangle"
											helpText="Price cannot be determined until the Insurance step is completed."
											large="true"
										/>
									);
								} else if (price) {
									if (!price.items || price.items.filter(item=>!item.feeItem).length === 0) {
										// If there are no price itmes set the priceMarkup. This will be used to create a single item breakdown.
										priceMarkup = '$' + parseFloat(Math.round(price.price * 100) / 100).toFixed(2);
									} else {
										// Loop through the price items and create a new breakdown entry for each
										breakdown = price.items.filter(item=>!item.feeItem).map(item => {
											return {
												reqId: req.id,
												reqNumber: req.reqNumber,
												itemDescription: item.name,
												price: '$' + parseFloat(Math.round(item.price * 100) / 100).toFixed(2)
											};
										});
									}
								} else {
									priceMarkup = (
										<FieldLevelHelp
											trigger="hover"
											textClassName="text-info"
											iconClassName="fa-info-circle"
											helpText="Price cannot be determined until the Billing Options step is completed."
											large="true"
										/>
									);
								}

								// No price breakdown was created. Create one with a single item
								if (!breakdown) {
									breakdown = [
										{
											reqId: req.id,
											reqNumber: req.reqNumber,
											itemDescription: req.omsOrderableItemName,
											price: priceMarkup
										}
									];
								}

								/**
								 * Return a new row for each item in the price breakdown.
								 * The first row will include the req number and edit/delete buttons. This row will span
								 *  over any subsequent rows.
								 */
								const borderStyle = { borderTop: '1px solid rgb(200,200,200)' };
								const noBorderStyle = { borderTop: 'none', paddingTop: '0' };
								return breakdown.map((item, index, breakdown) => {
									// SCR-6770: Allow clients to delete reqs until they have been accessioned
									const canDeleteReq = canEdit || (this.props.userInfo.type === 'myGDX' && !req.accessionNo);
									return (
										<tr key={index}>
											{index === 0 && (
												<td rowSpan={breakdown.length} style={borderStyle}>
													{item.reqNumber}
													{(canEdit || canDeleteReq) && (
														<span>
															{item.reqNumber && <br />}
															{/* Do not allow editing of Physician in the Loop requisitions */}
															{(canEdit && !this.props.gearOrder?.physicianInTheLoop) && (
																<button
																	type="button"
																	className="shipping-cart-action btn btn-link btn-unstyled"
																	onClick={() => this.editRequisition(req.id)}
																>
																	<i className="fa fa-pencil-square-o padding-x-xs"></i>
																</button>
															)}
															{canDeleteReq && (
																<button
																	type="button"
																	className="shipping-cart-action btn btn-link btn-unstyled"
																	onClick={() => this.displayRequisitionDeleteDialog(req)}
																>
																	<i className="fa fa-trash-o padding-x-xs"></i>
																</button>
															)}
														</span>
													)}
												</td>
											)}
											<td style={index === 0 ? borderStyle : noBorderStyle}>
												{item.itemDescription}
											</td>
											<td
												className="text-right"
												style={index === 0 ? borderStyle : noBorderStyle}
											>
												{this.state.domesticRequisitionType && (
													<span style={{ display: 'inline-block', marginBottom: '0.2em' }}>
														{item.price}
													</span>
												)}
											</td>
										</tr>
									);
								});
							})}
						</tbody>
						{!this.props.gearOrder.containsShipToOfficeRequisition &&
							this.state.domesticRequisitionType &&
							!this.props.doNotQuotePrices && (
								<tfoot>
									<tr>
										<th colSpan="2" className="text-right">
											Payment Due Today
										</th>
										<th className="text-right">{totalDueToday}</th>
									</tr>
								</tfoot>
							)}
					</table>
				</div>
				{this.state.displayRemoveRequisition && (
					<RemoveRequisition
						gearOrderId={this.props.gearOrder.id}
						requisition={this.state.reqToDelete}
						removeRequisitionResponseHandler={this.removeRequisitionResponseHandler}
						modalHidden={() => this.setState({ displayRemoveRequisition: false })}
					/>
				)}
			</div>
		);
	}
}

// wrap the component in the workflow step HOC to get access to common workflow logic
export default withPricing(withWorkflow(ShoppingCart));
